/**
 * Wainscot end panels (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 3d) — the one rule the 3D view, the elevation, the panel and the
 * estimate read, so what is drawn is what is billed.
 *
 * A run that asks for them (`run.endPanels: { style: 'wainscot' }`) dresses
 * every EXPOSED side of its cabinets with a door-style panel ¾" thick: a
 * side no wall and no solid neighbour stands against for its whole depth
 * (`pieces.exposedStretches`, the crown's own test, over the side's height).
 * Venus sells four (catalog p.27), each ½" under the side it is applied to:
 *
 *   - `WP-BASE` 23½" × 29½" on a 24"-deep base, above its toe kick;
 *   - `WP-W30` / `W36` / `W42` 11½" × 29½" / 35½" / 41½" on a 12"-deep wall
 *     cabinet 30", 36" or 42" tall.
 *
 * A generic line offers the same four at our figure. A side no panel fits
 * (a 15" upper, a 24"-deep one over the fridge, a vanity base) stays plain,
 * with the reason; so does one in a finish the line does not sell the panel
 * in — never another colour (`resolveTrimProfile`). A filler ending a run
 * takes one on its outer side; tall cabinets, corner units and floating
 * shelves take none. A floating-shelf bay never
 * covers the side beside it: its boards leave that side in view.
 *
 * Pure; metres.
 */
const { familyOf } = require('../cabinet-runs/families');
const { familySizes } = require('../cabinet-runs/vendorSizes');
const { findRunByIdOrAlias } = require('../cabinet-runs/runIds');
const { CABINET } = require('../cabinet-runs/cabinetFrontSpecs');
const { isFloatingShelves } = require('../cabinet-runs/floatingShelves');
const { isRack } = require('../cabinet-runs/racks');
const { trimProfiles, resolveTrimProfile, inchesText } = require('./profiles');
const { carrierOutlines } = require('./fronts');
const { exposedStretches, wallSegments, trimColoursFor } = require('./pieces');

const IN = 0.0254;
const THICKNESS_M = 0.75 * IN;
// a panel is this much under the side it dresses, both ways (the catalog's)
const UNDER_M = 0.5 * IN;
// …give or take this, a side's measure against a panel's
const FIT_M = 1 * IN;
// heights within this are the same (the trim's)
const SAME_M = 0.01;
// a side open for all but this of its depth is open
const OPEN_M = 0.01;

// Bodies that close a cabinet side standing against it — the fill's
// `CLOSES_A_SIDE` and what hangs beside an upper. An outlet on an island's end
// or a stool by it does not.
const SOLID =
  /cabinet|blank-panel|filler|panel|range|oven|refrigerator|fridge|dishwasher|storage|cooktop|washer|dryer|hood|surround|microwave|freezer|wine/;

const topOf = (o) => o.center.y + o.dimensions.y / 2;
const bottomOf = (o) => o.center.y - o.dimensions.y / 2;
const dot = (a, b) => a.x * b.x + a.z * b.z;
const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
const hasGeometry = (o) =>
  o?.center &&
  [o.dimensions?.x, o.dimensions?.y, o.dimensions?.z].every((v) => v > 0);

/**
 * The tier of a cabinet that may wear a panel, or null for none. A filler
 * may: where it ends a run, its outer side IS the run's end.
 */
function tierOf(o) {
  // floating shelves have no side; an end shelf IS the run's end
  if (isFloatingShelves(o) || o?.category?.endShelf) return null;
  // a wall cabinet with drawers stands on the counter: no panel is its size;
  // a valance bay is a board, no cabinet
  if (o?.category?.wallDrawers || o?.category?.valanceBay) return null;
  // an open rack's sides are its own finished boards (`racks.js`), its
  // stacked cabinet is no size a panel comes in (`rackStacks.js`), and an
  // angled end's open side is its angled face (`angledEnds.js`)
  if (isRack(o) || o?.category?.rackCompanion || o?.category?.angledEnd)
    return null;
  if (o.type === 'lower-cabinet') return 'base';
  if (o.type === 'upper-cabinet') return 'wall';
  return null;
}

/** The band of a cabinet's side a panel covers: above a base's toe kick. */
function sideBand(o, tier) {
  const bottom = bottomOf(o) + (tier === 'base' ? CABINET.toeKickHeight : 0);
  return { bottomY: bottom, topY: topOf(o) };
}

/** The panel of this tier that fits a side `depthM` × `heightM`, or null. */
function panelFor(profiles, tier, depthM, heightM) {
  return (
    profiles.find(
      (p) =>
        p.tier === tier &&
        Math.abs(p.widthM + UNDER_M - depthM) <= FIT_M &&
        Math.abs(p.heightM + UNDER_M - heightM) <= FIT_M
    ) || null
  );
}

// "a 34½" base and a 12" deep wall cabinet 30", 36" or 42" tall"
function sizesText(profiles) {
  const base = profiles.find((p) => p.tier === 'base');
  const walls = profiles.filter((p) => p.tier === 'wall');
  const heights = walls.map((p) => inchesText(p.heightM / IN + 0.5));
  const list =
    heights.length > 1
      ? `${heights.slice(0, -1).join(', ')} or ${heights[heights.length - 1]}`
      : heights.join('');
  const parts = [];
  if (base) parts.push('a 34½" base');
  if (walls.length)
    parts.push(
      `a ${inchesText(walls[0].widthM / IN + 0.5)} deep wall cabinet ${list} tall`
    );
  return parts.join(' and ');
}

const inchesOf = (m) => inchesText(Math.round((m / IN) * 8) / 8);

/** The way a cabinet's front faces (local +Z). */
function facingOf(o) {
  const ry = o.rotation?.y || 0;
  return { x: Math.sin(ry), z: Math.cos(ry) };
}

/**
 * The exposed sides of one cabinet: each `{ a, b, normal }` stretch along a
 * side (perpendicular to its front) open for the side's whole depth.
 */
function exposedSides(o, covering, walls) {
  const facing = facingOf(o);
  return exposedStretches(o, covering, walls).filter(
    (s) =>
      Math.abs(dot(s.normal, facing)) < 1e-6 &&
      dist(s.a, s.b) >= o.dimensions.z - OPEN_M
  );
}

/**
 * The door style most of these cabinets' doors wear — what a doorless one's
 * panel (a filler's) is drawn in; null where none has a door.
 */
function usualDoorStyle(cabinets) {
  const counts = new Map();
  for (const o of cabinets)
    if (!o.category?.filler && o.doorStyle)
      counts.set(o.doorStyle, (counts.get(o.doorStyle) || 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/**
 * One panel on one exposed side, placed: its face out along `normal`, in
 * `look` — its `colourCode` and the `doorStyle` it is drawn in.
 */
function placedPanel(run, cabinet, side, profile, band, look) {
  const mid = { x: (side.a.x + side.b.x) / 2, z: (side.a.z + side.b.z) / 2 };
  const out = side.normal;
  return {
    id: `${run.id}-end-panel-${cabinet.id}-${out.x.toFixed(3)}_${out.z.toFixed(3)}`,
    runId: run.id,
    cabinetId: cabinet.id,
    profileId: profile.id,
    sku: profile.sku,
    tier: profile.tier,
    colourCode: look.colourCode,
    doorStyle: look.doorStyle,
    center: {
      x: mid.x + (out.x * THICKNESS_M) / 2,
      y: (band.bottomY + band.topY) / 2,
      z: mid.z + (out.z * THICKNESS_M) / 2,
    },
    dimensions: { x: profile.widthM, y: profile.heightM, z: THICKNESS_M },
    rotation: { x: 0, y: Math.atan2(out.x, out.z), z: 0 },
  };
}

/** Every panel on one run, and the sides it leaves plain with the reason. */
function runEndPanels(run, objects, walls) {
  const family = familyOf(run);
  const profiles = trimProfiles(family, 'end-panel');
  const vendor = !!familySizes(family);
  const inRun = (o) => !!o.runId && !!findRunByIdOrAlias([run], o.runId);
  const dressed = objects.filter((o) => inRun(o) && tierOf(o));
  // each one's colour, a filler's the run's where its own cannot be read —
  // the trim's rule (`trimColoursFor`)
  const colours = vendor ? trimColoursFor(family, dressed) : [];
  // …and the door style it is drawn in: its own, a filler's the run's
  const runDoorStyle = usualDoorStyle(dressed);
  const doorStyleOf = (o) =>
    o.category?.filler
      ? (runDoorStyle ?? o.doorStyle ?? null)
      : (o.doorStyle ?? runDoorStyle);
  const panels = [];
  const notes = [];
  dressed.forEach((cabinet, i) => {
    const tier = tierOf(cabinet);
    const band = sideBand(cabinet, tier);
    const covering = objects
      .filter(
        (o) =>
          o !== cabinet &&
          SOLID.test(o.type || '') &&
          !isFloatingShelves(o) &&
          topOf(o) > band.bottomY + SAME_M &&
          bottomOf(o) < band.topY - SAME_M
      )
      .flatMap(carrierOutlines);
    const sides = exposedSides(cabinet, covering, walls);
    if (sides.length === 0) return;
    const heightM = band.topY - band.bottomY;
    const fit = panelFor(profiles, tier, cabinet.dimensions.z, heightM);
    let reason = null;
    let profile = null;
    let colourCode = null;
    if (!fit) {
      reason = `${family.name} sells wainscot panels for ${sizesText(profiles)}: this side is ${inchesOf(heightM)} tall and ${inchesOf(cabinet.dimensions.z)} deep, so it stays plain`;
    } else {
      colourCode = vendor ? (colours[i] ?? null) : null;
      const used = resolveTrimProfile(
        family,
        'end-panel',
        fit.id,
        vendor ? [colourCode] : []
      );
      profile = used.profile;
      reason = used.reason ?? null;
    }
    for (const side of sides) {
      if (profile)
        panels.push(
          placedPanel(run, cabinet, side, profile, band, {
            colourCode,
            doorStyle: doorStyleOf(cabinet),
          })
        );
      else notes.push({ runId: run.id, cabinetId: cabinet.id, reason });
    }
  });
  return { panels, notes };
}

/**
 * Every wainscot end panel in the room, and the exposed sides left plain.
 *
 * @param {Object} room  `objects`, `walls`, `cabinetRuns`
 * @returns {{ panels: EndPanel[], notes: { runId, cabinetId, reason }[] }}
 */
function cabinetEndPanels(room) {
  const objects = (room?.objects || []).filter(hasGeometry);
  const walls = wallSegments(room);
  const panels = [];
  const notes = [];
  for (const run of room?.cabinetRuns || []) {
    if (run?.endPanels?.style !== 'wainscot') continue;
    const out = runEndPanels(run, objects, walls);
    panels.push(...out.panels);
    notes.push(...out.notes);
  }
  return { panels, notes };
}

module.exports = { END_PANEL_THICKNESS_M: THICKNESS_M, cabinetEndPanels };
